'use client';

import { useEffect, useRef } from 'react';
import { useElevarEvents } from '../../elevar';
import {
  resetCartViewTracking,
  trackViewCartGlobal,
} from '../../elevar/cart-view-tracker';
import { useCart } from '../context';
import { useShopifyCart } from './use-shopify-cart';

export function useCartViewTracking({ domain }: { domain: string }) {
  const { open, loading } = useCart();
  const { data, isLoading, isFetching } = useShopifyCart();
  const { trackViewCart } = useElevarEvents();
  const previousOpenRef = useRef(false);

  // Track view cart when we have fresh data after opening
  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    const isNowOpen = open;

    // Update the previous state
    previousOpenRef.current = isNowOpen;

    // Reset global tracking when cart closes
    if (!isNowOpen) {
      resetCartViewTracking();
      return;
    }

    // Don't track if cart data isn't ready
    if (!data?.shopifyCart || isLoading || isFetching) {
      return;
    }

    // Don't track if add-to-cart operation is in progress
    if (loading) {
      return;
    }

    // Track in these scenarios:
    // 1. Cart just opened and no add-to-cart in progress (user clicked cart icon)
    // 2. Cart is open and add-to-cart just completed (loading changed to false)
    const justOpened = !wasOpen && isNowOpen;
    const shouldTrack = justOpened || isNowOpen;

    if (!shouldTrack) {
      return;
    }

    trackViewCartGlobal({
      cartData: data.shopifyCart,
      domain,
      trackViewCart,
    });
  }, [
    open,
    loading,
    data?.shopifyCart,
    isLoading,
    isFetching,
    trackViewCart,
    domain,
  ]);
}
